import {IGetMetadata} from "../types/metadata";
import {useQuery} from "@tanstack/react-query";
import Api from "../api/Api";
import moment from "moment";
import {DT0Format} from "../utils/StaticVariable";
import {GLOBAL_HD_DTU_ID} from "../utils/common";
import {QUERY_KEYS} from "./queryKeys";
import {IRosePlotPreviewData} from "../types/analytics";

export const useRosePlotQuery = (
  datasetId: string,
  coordinates: number[],
  pointName: string,
  timeStepInterval: string,
  metadata: Partial<IGetMetadata>,
  timeSeriesData: any,
  isTimeSeriesFinished: boolean,
) => {
  return useQuery({
    queryKey: [
      QUERY_KEYS.plot.roseplot,
      datasetId,
      coordinates,
      pointName,
      timeStepInterval,
    ],
    queryFn: async () => {
      if (metadata?.IsOffline || !metadata.TimeDefinition || !metadata.ItemsInfo || !metadata) {
        return { success: false, error: 'Invalid validation result' }
      }

      const timeRange = metadata.TimeDefinition.split(';')

      try {
        const defaultShortName = metadata?.ItemsInfo?.find(
          (item: any) => item.ShortName === metadata.DefaultValueShortName
        )?.Name ?? metadata.ItemsInfo[0].Name

        const formattedDefaultShortName = defaultShortName.replace(/ /g, "_")

        let rosePlotDirection = 1

        if(timeSeriesData){
          const getTimeSeriesShortName = timeSeriesData?.Data?.Items?.map((item: any) => {
            return parseFloat(item[formattedDefaultShortName])
          }).filter((value: any) => !isNaN(value) && value !== null)
          const maxValue = getTimeSeriesShortName.length > 0 ? Math.max(...getTimeSeriesShortName) : 1

          rosePlotDirection = parseFloat((maxValue / 8).toFixed(2))
        }

        const rosePlotPreviewData = await Api.chart.getRosePlotV2(
          crypto.randomUUID(),
          pointName,
          datasetId,
          moment(timeRange[1]).subtract(1, 'year').format(DT0Format),
          timeRange[1],
          coordinates.join(),
          datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
          rosePlotDirection
        )

        if (rosePlotPreviewData?.RosePlot === null) {
          return {
            success: false,
            error: 'Failed to return preview rose data',
            direction: rosePlotDirection
          }
        }

        if (rosePlotPreviewData) {
          return {
            success: true,
            direction: rosePlotDirection,
            data: {
              RosePlot: rosePlotPreviewData,
              ItemsInfo: metadata.ItemsInfo,
              DefaultDirectionShortName: metadata.DefaultDirectionShortName,
              DefaultValueShortName: metadata.DefaultValueShortName,
            } as IRosePlotPreviewData
          }
        }

        return {
          success: false,
          error: 'No rose plot data returned',
          direction: rosePlotDirection
        }
      } catch (error) {
        console.error("Error fetching rose plot preview:", error)
        return {
          success: false,
          error: 'Failed to return preview rose data'
        }
      }
    },
    enabled: !metadata?.IsOffline && isTimeSeriesFinished && !!datasetId && !!coordinates.length,
  })
}
